import type { L10n } from '../types'
import type { IntegrationProviderId } from '../types/integrations'
import type { TeamRole } from './admin'
import { HYDRATION_ML_PER_KG } from '../utils/hydration'

/* ------------------------------------------------------------------ *
 * Special (capability) permissions
 * Granted per role in addition to the area × action matrix.
 * REQUIRES BACKEND: every check below is enforced server-side; hiding UI
 * is never authorization.
 * ------------------------------------------------------------------ */

export type CapabilityPermission =
  | 'integrations.view'
  | 'integrations.manage'
  | 'hydration.viewOps'
  | 'health.viewSensitive'
  | 'rules.manageHydration'
  | 'engagement.manageConfig'

export const capabilityLabels: Record<CapabilityPermission, { label: L10n; hint: L10n }> = {
  'integrations.view': {
    label: { 'pt-BR': 'Ver status das integrações', 'en-US': 'View integration status' },
    hint: { 'pt-BR': 'Status, contas conectadas e sincronização (agregados).', 'en-US': 'Status, connected accounts and sync (aggregated).' },
  },
  'integrations.manage': {
    label: { 'pt-BR': 'Gerenciar integrações', 'en-US': 'Manage integrations' },
    hint: { 'pt-BR': 'Alterar status de um provedor. Exige confirmação e fica auditado.', 'en-US': 'Change a provider status. Requires confirmation and is audited.' },
  },
  'hydration.viewOps': {
    label: { 'pt-BR': 'Ver dados operacionais de hidratação', 'en-US': 'View hydration operational data' },
    hint: { 'pt-BR': 'Meta calculada, água registrada hoje e método de conclusão.', 'en-US': 'Calculated target, water logged today and completion method.' },
  },
  'rules.manageHydration': {
    label: { 'pt-BR': 'Alterar regra de hidratação', 'en-US': 'Change hydration rule' },
    hint: { 'pt-BR': 'Publicar nova versão do coeficiente. Regra controlada de produto.', 'en-US': 'Publish a new coefficient version. Controlled product rule.' },
  },
  'engagement.manageConfig': {
    label: { 'pt-BR': 'Gerenciar parâmetros de engajamento', 'en-US': 'Manage engagement parameters' },
    hint: { 'pt-BR': 'Proteção de Sequência, Colmeia, Baú e Liga. Regras fixas nunca são editáveis. Exige confirmação e fica auditado.', 'en-US': 'Streak Protection, Hive, Chest and League. Fixed rules are never editable. Requires confirmation and is audited.' },
  },
  'health.viewSensitive': {
    label: { 'pt-BR': 'Ver dados sensíveis de saúde', 'en-US': 'View sensitive health data' },
    hint: { 'pt-BR': 'Biomarcadores, testosterona e composição corporal individuais. Desligada por padrão para todos os papéis; concessão individual, com justificativa e auditoria.', 'en-US': 'Individual biomarkers, testosterone and body composition. Off by default for every role; granted individually, with justification and audit.' },
  },
}

export const capabilityOrder: CapabilityPermission[] = [
  'integrations.view',
  'integrations.manage',
  'hydration.viewOps',
  'rules.manageHydration',
  'engagement.manageConfig',
  'health.viewSensitive',
]

/** Role defaults. `health.viewSensitive` is off for every role by design. */
export const capabilityMatrix: Record<TeamRole, CapabilityPermission[]> = {
  superadmin: ['integrations.view', 'integrations.manage', 'hydration.viewOps', 'rules.manageHydration', 'engagement.manageConfig'],
  admin: ['integrations.view', 'hydration.viewOps'],
  moderator: [],
  content: [],
  analyst: ['integrations.view'],
  support: ['integrations.view', 'hydration.viewOps'],
}

/* ------------------------------------------------------------------ *
 * PROTOTYPE-ONLY role preview ("Modo de demonstração").
 * Lets reviewers see permission gating per role. It does NOT change the
 * permission model and must not ship: in production the role comes from the
 * authenticated session and every check is enforced by the backend.
 * ------------------------------------------------------------------ */

export const PROTOTYPE_QA_MODE = true

export const prototypePreviewRoles: TeamRole[] = ['superadmin', 'admin', 'moderator', 'analyst', 'support']

/** Previewed role. Default: Admin (Bruno Sato). Live binding — read at render. */
export let prototypeAdminRole: TeamRole = 'admin'

const previewActors: Record<TeamRole, string> = {
  superadmin: 'Marina Alencar',
  admin: 'Bruno Sato',
  moderator: 'Helena Prado',
  content: 'Cecília Nardi',
  analyst: 'Otávio Reis',
  support: 'Lia Fontes',
}

export function setPrototypeAdminRole(role: TeamRole) {
  prototypeAdminRole = role
}

/** Name of the team member the preview acts as (used by the prototype audit writer). */
export function prototypeActorName(role: TeamRole = prototypeAdminRole): string {
  return previewActors[role]
}

export function hasCapability(role: TeamRole, capability: CapabilityPermission): boolean {
  // Sensitive health access is never inherited from a role — only an
  // individual, justified, audited grant enables it (REQUIRES BACKEND).
  if (capability === 'health.viewSensitive') return false
  return capabilityMatrix[role].includes(capability)
}

/* ------------------------------------------------------------------ *
 * Hydration rule — controlled product rule with version history.
 * The active coefficient comes from utils/hydration (single source).
 * ------------------------------------------------------------------ */

export interface HydrationRuleVersion {
  version: string
  coefficientMlPerKg: number
  status: 'active' | 'retired' | 'draft'
  effectiveFrom: string
  publishedBy: string
  note: L10n
}

export const hydrationRuleVersions: HydrationRuleVersion[] = [
  {
    version: 'v1',
    coefficientMlPerKg: HYDRATION_ML_PER_KG,
    status: 'active',
    effectiveFrom: '28/09/2026',
    publishedBy: 'Marina Alencar',
    note: { 'pt-BR': 'Meta personalizada por peso substitui a meta fixa de 2 L.', 'en-US': 'Weight-based target replaces the fixed 2 L target.' },
  },
]

/* ------------------------------------------------------------------ *
 * Integrations — operational status (Admin view).
 * No provider is live. Operational fields stay null until one is.
 * ------------------------------------------------------------------ */

export type AdminIntegrationStatus = 'comingSoon' | 'disabled' | 'testing' | 'active'

export const integrationStatusLabels: Record<AdminIntegrationStatus, L10n> = {
  comingSoon: { 'pt-BR': 'Em breve', 'en-US': 'Coming soon' },
  disabled: { 'pt-BR': 'Desativada', 'en-US': 'Disabled' },
  testing: { 'pt-BR': 'Em teste', 'en-US': 'Testing' },
  active: { 'pt-BR': 'Ativa', 'en-US': 'Active' },
}

export interface AdminIntegration {
  id: IntegrationProviderId
  name: string
  label?: L10n
  status: AdminIntegrationStatus
  connectedAccounts: number | null
  lastSync: string | null
  syncErrors24h: number | null
  importStatus: L10n | null
  crossPost: 'supported' | 'notSupported' | 'planned'
}

export const adminIntegrations: AdminIntegration[] = [
  { id: 'apple-health', name: 'Apple Health', status: 'comingSoon', connectedAccounts: null, lastSync: null, syncErrors24h: null, importStatus: null, crossPost: 'notSupported' },
  { id: 'google-health', name: 'Google Health Connect', status: 'comingSoon', connectedAccounts: null, lastSync: null, syncErrors24h: null, importStatus: null, crossPost: 'notSupported' },
  { id: 'garmin', name: 'Garmin', status: 'comingSoon', connectedAccounts: null, lastSync: null, syncErrors24h: null, importStatus: null, crossPost: 'notSupported' },
  { id: 'strava', name: 'Strava', status: 'comingSoon', connectedAccounts: null, lastSync: null, syncErrors24h: null, importStatus: null, crossPost: 'planned' },
  { id: 'other', name: 'Other', label: { 'pt-BR': 'Outras plataformas', 'en-US': 'Other platforms' }, status: 'comingSoon', connectedAccounts: null, lastSync: null, syncErrors24h: null, importStatus: null, crossPost: 'planned' },
]

/**
 * Prototype: change an integration's administrative status. Callers must have
 * `integrations.manage`, confirm, and write an audit record. This never opens
 * a real external connection. REQUIRES BACKEND: authorization + persistence.
 */
export function setIntegrationStatus(id: IntegrationProviderId, status: AdminIntegrationStatus) {
  const service = adminIntegrations.find((item) => item.id === id)
  if (service) service.status = status
}

/* ------------------------------------------------------------------ *
 * Per-user operational snapshot (User Detail).
 * No biomarker or body-composition value lives here.
 * ------------------------------------------------------------------ */

export interface UserHydrationOps {
  weightAvailable: boolean
  /** Target is computed by the backend from the latest weight; shown, never stored here as a weight. */
  targetMl: number | null
  loggedTodayMl: number
  completion: 'automatic' | 'manual' | null
}

export interface UserConnection {
  provider: IntegrationProviderId
  status: AdminIntegrationStatus
  lastSync: string | null
}

export function userHydrationOps(userIndex: number): UserHydrationOps {
  const noWeight = userIndex % 5 === 3
  const targetMl = noWeight ? null : [2180, 2450, 2800, 2030, 2620][userIndex % 5]
  const loggedTodayMl = [1400, 2450, 900, 0, 2700][userIndex % 5]
  const completion: UserHydrationOps['completion'] = noWeight
    ? userIndex % 2 === 0
      ? 'manual'
      : null
    : targetMl !== null && loggedTodayMl >= targetMl
      ? 'automatic'
      : null
  return { weightAvailable: !noWeight, targetMl, loggedTodayMl, completion }
}

/** No integration is live, so every user currently has no connections. */
export function userConnections(_userId: string): UserConnection[] {
  return []
}
